import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Logo from "../../assets/restaurant/logo.png";
import { Menu as MenuIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const getInitialSection = () => {
	const { pathname, hash } = window.location;
	if (pathname === "/tiendas/demo") return "demo";
	if (hash) return hash.replace("#", "");
	if (pathname === "/tiendas" || pathname === "/tiendas/") return "inicio";
	return "inicio";
};

const Menu = () => {
	const primaryColor = "#43bd98";
	const accent = "#43bd98";
	const [menuOpen, setMenuOpen] = useState(false);
	const [activeSection, setActiveSection] = useState(getInitialSection);
	const menuRef = useRef(null);
	const { t } = useTranslation();
	const menuItems = [
		{ label: t("menu.home"), path: "/tiendas" },
		{ label: t("menu.features"), id: "funcionalidades" },
		{ label: t("menu.demo"), path: "/tiendas/demo" },
		{ label: t("menu.examples"), id: "ejemplos" },
		{ label: t("menu.pricing"), id: "precios" },
		{ label: t("menu.contact"), id: "contacto" },
	];

	const toggleMenu = () => setMenuOpen((prev) => !prev);
	const isDemo = window.location.pathname === "/tiendas/demo";

	useLayoutEffect(() => {
		const hash = window.location.hash.replace("#", "");
		if (!hash) return;
		const el = document.getElementById(hash);
		if (el) el.scrollIntoView({ behavior: "smooth" });
	}, []);

	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash.replace("#", "");
			if (hash) {
				setActiveSection(hash);
				const el = document.getElementById(hash);
				if (el) el.scrollIntoView({ behavior: "smooth" });
			} else {
				setActiveSection("inicio");
			}
		};
		window.addEventListener("hashchange", handleHashChange);
		return () => window.removeEventListener("hashchange", handleHashChange);
	}, []);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				setMenuOpen(false);
			}
		};

		if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [menuOpen]);

	const handleClick = (e, item) => {
		e.preventDefault();

		if (item.path) {
			window.location.href = item.path;
			return;
		}

		if (item.id) {
			if (isDemo) {
				window.location.href = `/tiendas/#${item.id}`;
			} else {
				window.location.hash = item.id;
			}
		}

		setMenuOpen(false);
	};

	const renderLinks = (isMobile = false) =>
		menuItems.map((item) => {
			let isActive = false;

			if (item.label === "Inicio") {
				isActive =
					activeSection === "inicio" &&
					(window.location.pathname === "/tiendas" ||
						window.location.pathname === "/tiendas/");
			} else if (item.path === "/tiendas/demo") {
				isActive = isDemo;
			} else if (item.id && activeSection === item.id) {
				isActive = true;
			}

			const commonStyle = {
				textDecoration: "none",
				color: isActive ? accent : "#444",
				fontWeight: isActive ? 600 : 500,
				fontSize: isMobile ? "1.1rem" : "1rem",
				padding: isMobile ? "0.8rem 0" : "0.3rem 0",
				transition: "color 0.3s ease",
				cursor: "pointer",
			};

			return (
				<a
					key={item.label}
					href={item.path || `/tiendas/#${item.id}`}
					style={commonStyle}
					onClick={(e) => handleClick(e, item)}
				>
					{item.label}
				</a>
			);
		});

	return (
		<header
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100%",
				padding: "1rem 1.5rem",
				backgroundColor: "#ffffff",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
				zIndex: 100,
				fontFamily: "'Poppins', sans-serif",
				boxSizing: "border-box",
			}}
		>
			<a href="/tiendas" style={{ display: "flex", alignItems: "center" }}>
				<img src={Logo} alt="Logo Applaby" style={{ height: "40px" }} />
			</a>

			<button
				onClick={(e) => {
					e.stopPropagation();
					toggleMenu();
				}}
				style={{
					background: "none",
					border: "none",
					cursor: "pointer",
					display: "none",
					fontSize: "1.8rem",
				}}
				className="menu-toggle"
			>
				{menuOpen ? <X size={24} /> : <MenuIcon size={24} />}
			</button>

			<nav
				style={{
					display: "flex",
					alignItems: "center",
					gap: "2.2rem",
				}}
				className="nav-desktop"
			>
				{renderLinks(false)}

				<a
					href="https://creator.applaby.com?onboarding=true&appType=Ecommerce"
					style={{
						padding: "0.75rem 1.6rem",
						background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
						color: "#fff",
						borderRadius: "8px",
						fontSize: "1rem",
						fontWeight: 600,
						textDecoration: "none",
						whiteSpace: "nowrap",
						transition: "all 0.3s ease",
						boxShadow: "0 4px 12px rgba(67,189,152,0.25)",
					}}
					target="_blank"
					rel="noreferrer"
				>
					{t("menu.start")}
				</a>
			</nav>

			{menuOpen && (
				<div
					ref={menuRef}
					onClick={(e) => e.stopPropagation()}
					style={{
						position: "absolute",
						top: "100%",
						right: 0,
						backgroundColor: "#fff",
						width: "100%",
						boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
						display: "flex",
						flexDirection: "column",
						padding: "1rem 1.5rem",
						gap: "1rem",
						animation: "slideDown 0.3s ease forwards",
					}}
					className="nav-mobile"
				>
					{renderLinks(true)}
					<a
						href="https://creator.applaby.com?onboarding=true&appType=Ecommerce"
						style={{
							padding: "0.9rem 1.5rem",
							backgroundColor: primaryColor,
							color: "white",
							borderRadius: "6px",
							fontSize: "1.05rem",
							textAlign: "center",
							textDecoration: "none",
							fontWeight: 600,
							transition: "all 0.3s ease",
						}}
					>
						Empieza gratis
					</a>
				</div>
			)}

			<style>{`
				@media (max-width: 992px) {
					.nav-desktop { display: none !important; }
					.menu-toggle { display: block !important; }
				}
				@keyframes slideDown {
					from { opacity: 0; transform: translateY(-10px); }
					to { opacity: 1; transform: translateY(0); }
				}
				[id] {
					scroll-margin-top: 90px;
				}
			`}</style>
		</header>
	);
};

export default Menu;
